import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import type {
  PatientAllergy,
  PatientFieldsDraft,
  StaffingPreferencesBody,
} from "./staffingPreferences.types";
import {
  createPatientFieldsDraft,
  createStaffingPreferencesDraft,
  getAllergyOptions,
} from "./staffingPreferences.utils";

export function usePatientProfileStaffingPreferencesController(patient: PatientProfilePatient) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const canEdit = auth.isAllowedTo("edit_patient_page_summary_details");
  const [draft, setDraft] = React.useState<StaffingPreferencesBody | null>(null);
  const [patientFieldsDraft, setPatientFieldsDraft] = React.useState<PatientFieldsDraft>(() =>
    createPatientFieldsDraft(patient)
  );
  const staffingPreferencesQuery = useQuery({
    queryKey: ["patientProfileModal", patient.id, "staffingPreferences"],
    queryFn: () =>
      api.get("./patients/:patientId/staffing_preferences", {
        path: { patientId: patient.id },
      }),
  });
  const allergyTypesQuery = useQuery({
    enabled: draft !== null,
    queryKey: ["patientProfileModal", "allergies"],
    queryFn: () => api.get("/agencies/:agencyId/allergies", {}),
  });
  const updateStaffingPreferencesMutation = useMutation({
    mutationFn: (body: StaffingPreferencesBody) =>
      api.put("./patients/:patientId/staffing_preferences", {
        path: { patientId: patient.id },
        body,
      }),
    onSuccess: (preferences) => {
      queryClient.setQueryData(
        ["patientProfileModal", patient.id, "staffingPreferences"],
        preferences
      );
      toast({
        description: "Staffing preferences updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update staffing preferences.",
        status: "error",
      });
    },
  });
  const updateMedicalNotesMutation = useMutation({
    mutationFn: (medicalNotes: string) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: {
          id: patient.id,
          medicalNotes: medicalNotes.trim() === "" ? null : medicalNotes,
        },
      }),
    onSuccess: (_, medicalNotes) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          medicalNotes: medicalNotes.trim() === "" ? null : medicalNotes,
        };
      });
      queryClient.invalidateQueries({ queryKey: ["patient", "get", patient.id] });
      toast({
        description: "Medical notes updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      setPatientFieldsDraft((currentDraft) => ({
        ...currentDraft,
        medicalNotes: patient.medicalNotes ?? "",
      }));
      toast({
        description: "Could not update medical notes.",
        status: "error",
      });
    },
  });
  const updateAllergiesMutation = useMutation({
    mutationFn: async (params: {
      nextAllergyIds: Array<PatientAllergy["id"]>;
      previousAllergyIds: Array<PatientAllergy["id"]>;
    }) => {
      const addedAllergyIds = params.nextAllergyIds.filter(
        (allergyId) => !params.previousAllergyIds.includes(allergyId)
      );
      const removedAllergyIds = params.previousAllergyIds.filter(
        (allergyId) => !params.nextAllergyIds.includes(allergyId)
      );

      if (addedAllergyIds.length > 0) {
        await api.put(
          "/agencies/:agencyId/agency_member/:agencyMemberId/patients/:patientId/allergies",
          {
            path: { patientId: patient.id },
            body: {
              allergyIds: addedAllergyIds,
              type: "ASSOCIATE",
            },
          }
        );
      }

      if (removedAllergyIds.length > 0) {
        await api.put(
          "/agencies/:agencyId/agency_member/:agencyMemberId/patients/:patientId/allergies",
          {
            path: { patientId: patient.id },
            body: {
              allergyIds: removedAllergyIds,
              type: "DISSOCIATE",
            },
          }
        );
      }

      return params.nextAllergyIds;
    },
    onSuccess: (nextAllergyIds) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        const allergyById = new Map<number, PatientAllergy>();

        patient.allergies.forEach((allergy) => allergyById.set(allergy.id, allergy));
        (allergyTypesQuery.data?.allergies ?? []).forEach((allergy) =>
          allergyById.set(allergy.id, allergy)
        );

        return {
          ...currentPatient,
          allergies: nextAllergyIds
            .map((allergyId) => allergyById.get(allergyId))
            .filter((allergy): allergy is PatientAllergy => allergy !== undefined),
        };
      });
      queryClient.invalidateQueries({ queryKey: ["patient", "get", patient.id] });
      toast({
        description: "Allergies updated successfully.",
        status: "success",
      });
    },
    onError: (_, params) => {
      setPatientFieldsDraft((currentDraft) => ({
        ...currentDraft,
        allergyIds: params.previousAllergyIds,
      }));
      toast({
        description: "Could not update allergies.",
        status: "error",
      });
    },
  });
  const preferences = staffingPreferencesQuery.data;
  const allergyOptions = getAllergyOptions(
    allergyTypesQuery.data?.allergies ?? [],
    patient.allergies
  );
  const commitDraft = (nextDraft: StaffingPreferencesBody) => {
    setDraft(nextDraft);
    updateStaffingPreferencesMutation.mutate(nextDraft);
  };
  const toggleEditMode = () => {
    setDraft((currentDraft) => {
      if (currentDraft !== null) {
        return null;
      }

      if (preferences === undefined) {
        return currentDraft;
      }

      setPatientFieldsDraft(createPatientFieldsDraft(patient));
      return createStaffingPreferencesDraft(preferences);
    });
  };
  const commitAllergies = (nextAllergyIds: Array<PatientAllergy["id"]> | undefined) => {
    const normalizedAllergyIds = nextAllergyIds ?? [];
    const previousAllergyIds = patientFieldsDraft.allergyIds;

    setPatientFieldsDraft((currentDraft) => ({
      ...currentDraft,
      allergyIds: normalizedAllergyIds,
    }));
    updateAllergiesMutation.mutate({
      nextAllergyIds: normalizedAllergyIds,
      previousAllergyIds,
    });
  };
  const commitMedicalNotes = (medicalNotes: string) => {
    setPatientFieldsDraft((currentDraft) => ({ ...currentDraft, medicalNotes }));
    updateMedicalNotesMutation.mutate(medicalNotes);
  };

  return {
    allergyOptions,
    canEdit,
    commitAllergies,
    commitDraft,
    commitMedicalNotes,
    draft,
    isAllergyOptionsLoading: allergyTypesQuery.isLoading,
    isEditing: draft !== null,
    patientFieldsDraft,
    preferences,
    refetchPreferences: staffingPreferencesQuery.refetch,
    setDraft,
    setPatientFieldsDraft,
    staffingPreferencesQuery,
    toggleEditMode,
  };
}
